import { environment } from 'src/environments/environment';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  ActionSheetController,
  IonContent,
  Platform,
  PopoverController,
} from '@ionic/angular';
import { PopoverListComponent } from 'src/app/components/popover-list/popover-list.component';
import { SeoService } from 'src/app/services/seo.service';
import { GlobalDataService } from 'src/app/services/global-data.service';
import { PostItemSEO, PostItemSocial } from 'src/app/types/post';
import { SEOSchema } from 'src/app/types/seo';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { JsonLdService } from 'src/app/services/json-ld.service';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.page.html',
  styleUrls: ['./contact.page.scss'],
})
export class ContactPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  lat: number;
  lng: number;
  destination: string;
  address: string;
  visitorInformationOpeningTimes: string;
  managementOfficeOpeningTimes: string;
  visitorInformationAddress: string;
  contactFormEmbedTemp: string = `<div data-paperform-id="${environment.contact_form_uuid}" height="600px"></div>`;
  metaDescription: string = "";

  visitorInformation;
  centreManagement;
  mapStyles: google.maps.MapTypeStyle = {
    featureType: 'all',
    elementType: 'all',
    stylers: [{ saturation: -100 }],
  };

  apiLoaded: Observable<boolean>;

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'ShoppingCenter',
    name: environment.environment_title,
    '@id': '',
    url: environment.environment_url,
    priceRange: 'ZAR',
  };

  mapOptions: google.maps.MapOptions;
  markerPosition: google.maps.LatLngLiteral;
  markerOptions: google.maps.MarkerOptions = {
    draggable: false,
    clickable: true,
  };

  constructor(
    private platform: Platform,
    private seo: SeoService,
    public actionSheetController: ActionSheetController,
    public popoverController: PopoverController,
    private globalData: GlobalDataService,
    private jsonLdService: JsonLdService,
    private datePipe: DatePipe,
    httpClient: HttpClient
  ) {
    this.apiLoaded = httpClient
      .jsonp(
        `https://maps.googleapis.com/maps/api/js?key=${environment.google_api_key}`,
        'callback'
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );

    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');

      this.globalData.dataChange.subscribe((value) => {
        this.setContactDetails(value);
      });
    });
  }

  /**
   * Open location on Google Maps
   */
  openMap() {
    window?.open(
      'https://www.google.com/maps/search/?api=1&query=' + this.destination
    );
  }

  /**
   * Set contact details according to relevant categorisation
   */
  setContactDetails(mallData) {
    if (mallData.general) {
      this.visitorInformationOpeningTimes = mallData.general.infoDeskHours;
      this.managementOfficeOpeningTimes =
        mallData.general.managementOfficeHours;
      this.visitorInformationAddress = mallData.general.mallAddress;

      if (environment.environment_name == 'somerset') {
        this.metaDescription =
          'Give us a call or send us a message will be sure to reply to your request. Click here on the Somerset Mall shopping centre website for all the contact details.';
      } else if (environment.environment_name == 'clearwater') {
        this.metaDescription =
          'Contact Clearwater Mall for any customer need/queries. Browse our Contact Us page for your convenience.';
      } else if (environment.environment_name == 'woodlands') {
        this.metaDescription =
          'Give us a call or send us a message will be sure to reply to your request. Click here on the Woodland shopping centre website for all the contact details.';
      } else if (environment.environment_name == 'rosebank') {
        this.metaDescription =
          'Get in touch with Rosebank Mall for inquiries, feedback, or assistance with your shopping experience.';
      } else if (environment.environment_name === 'tablebay') {
        this.metaDescription =
          'Get in touch with Table Bay Mall. Find our contact details, location, and how we can assist with your enquiries.';
      } else if (environment.environment_name === 'glen') {
        this.metaDescription =
          'Need help? Contact The Glen Shopping Centre in Johannesburg South for enquiries, trading hours, directions, and centre information.';
      }

      if (this.visitorInformationAddress) {
        let url = this.visitorInformationAddress.split('@');

        if (url[1]) {
          let at = url[1].split('z'),
            zero = at[0].split(',');

          const addressConstruct = url[0].split('/');
          this.address = decodeURIComponent(
            addressConstruct[addressConstruct.length - 2].replace(/\+/g, ' ')
          );

          this.lat = parseFloat(zero[0]);
          this.lng = parseFloat(zero[1]);
          this.destination = this.lat + ',' + this.lng;

          this.mapOptions = {
            center: { lat: this.lat, lng: this.lng },
            zoom: 13,
            scrollwheel: false,
            styles: [this.mapStyles],
            disableDoubleClickZoom: true,
            fullscreenControl: false,
            mapTypeControl: false,
          };
          this.markerPosition = { lat: this.lat, lng: this.lng };
        }
      }
    }

    // Visitor Information
    if (mallData.contactInformation?.visitorInformation) {
      this.visitorInformation =
        mallData.contactInformation.visitorInformation.map((section) => {
          return {
            email: section.email,
            title: section.department,
            tel: section.contactNumber,
          };
        });
    }

    // Centre Management
    if (mallData.contactInformation?.centreManagement) {
      this.centreManagement = mallData.contactInformation.centreManagement.map(
        (section) => {
          return {
            email: section.email,
            title: section.department,
            tel: section.contactNumber,
          };
        }
      );
    }

    if (!this.cordova) {
      const title =
        (environment.environment_name == 'clearwater'
          ? "Clearwater Mall | Contact Us & Get In Touch"
          : 'Contact Us | ' + environment.environment_title + ' Shopping Centre');

      const seo: PostItemSEO = {
        title,
        description: this.metaDescription,
        keywords: mallData.general.metaKeywords + ' contact shopping mall',
      };

      const social: PostItemSocial = {
        title,
        description: this.metaDescription,
        socialImage: mallData.social.openGraphImage,
      };

      // Title tag
      this.seo.setTitle(seo.title);

      // Meta tags
      this.seo.setMeta(seo);
      this.seo.setSEOMeta(social);
    }

    let schemaData = { ...this.schema };

    // Schema image
    if (mallData.social.socialImage || mallData.socialImage) {
      schemaData = {
        ...schemaData,
        image: mallData.social.socialImage || mallData.socialImage,
      };
    }

    if (this.visitorInformation) {
      const telephone = this.visitorInformation.filter((contact) =>
        contact.title.includes('Management')
      )[0]?.tel;

      schemaData = {
        ...schemaData,
        telephone: telephone
          ? telephone.replaceAll(')', '').replaceAll('(', '')
          : '',
      };
    }

    if (mallData.tradingTimes) {
      let tradingTimes = [];

      if (mallData.tradingTimes.tradingEnabledMonday)
        tradingTimes.push('Monday');
      if (mallData.tradingTimes.tradingEnabledTuesday)
        tradingTimes.push('Tuesday');
      if (mallData.tradingTimes.tradingEnabledWednesday)
        tradingTimes.push('Wednesday');
      if (mallData.tradingTimes.tradingEnabledThursday)
        tradingTimes.push('Thursday');
      if (mallData.tradingTimes.tradingEnabledFriday)
        tradingTimes.push('Friday');
      if (mallData.tradingTimes.tradingEnabledSaturday)
        tradingTimes.push('Saturday');
      if (mallData.tradingTimes.tradingEnabledSunday)
        tradingTimes.push('Sunday');

      if (tradingTimes.length > 0)
        schemaData = {
          ...schemaData,
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: tradingTimes,
            opens: mallData.tradingTimes.tradingOpeningTimeTuesday,
            closes: mallData.tradingTimes.tradingClosingTimeTuesday,
          },
        };
    }

    schemaData = {
      ...schemaData,
      address: {
        '@type': 'PostalAddress',
        streetAddress: environment.street_address,
        addressLocality: environment.address_locality,
        postalCode: environment.postal_code,
        addressCountry: environment.address_country,
      },
    };

    if (mallData.social) {
      let social = [];

      if (mallData.social.facebookPageURL)
        social.push(mallData.social.facebookPageURL);
      if (mallData.social.youtubeChannelURL)
        social.push(mallData.social.youtubeChannelURL);
      if (mallData.social.instagramAccountURL)
        social.push(mallData.social.instagramAccountURL);
      if (mallData.social.twitterPageURL)
        social.push(mallData.social.twitterPageURL);
      if (mallData.social.linkedInProfileURL)
        social.push(mallData.social.linkedInProfileURL);
      if (mallData.social.pinterestURL)
        social.push(mallData.social.pinterestURL);

      if (social.length > 0) schemaData = { ...schemaData, sameAs: social };
    }

    this.schema = this.schema = { ...this.schema, ...schemaData };
    this.jsonLdService.insertSchema(this.schema);
  }

  /**
   * Present map provider popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: PopoverListComponent,
      componentProps: {
        data: [
          {
            text: 'Google Maps',
            icon: 'map-outline',
            handler: () => {
              window?.open(
                this.visitorInformationAddress
                  ? this.visitorInformationAddress
                  : 'https://www.google.com/maps/search/?api=1&query=' +
                      this.destination
              );
              this.popoverController.dismiss();
            },
          },
          {
            text: 'Waze',
            icon: 'navigate-outline',
            handler: () => {
              window?.open(
                'https://waze.com/ul?ll=' +
                  this.destination +
                  '&navigate=yes&z=10'
              );
              this.popoverController.dismiss();
            },
          },
        ],
      },
      event: ev,
      translucent: true,
    });

    return await popover.present();
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }

  ngOnInit() {
    const initialMallData = this.globalData.getGlobalData();
    if (initialMallData) this.setContactDetails(initialMallData);
  }
}
